import { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { PipelineDetail, PipelineSummary } from '../types';
import { errMsg, unwrap } from '../editor/steps/SharedParts';

interface Props {
  clientUid: string;
  pipeline: Pick<PipelineSummary, 'uid' | 'name' | 'state'>;
  onChanged: (p: PipelineDetail) => void;
  size?: 'small' | 'medium';
}

/**
 * Pause / Resume for a published pipeline. Pausing is immediate — nothing is lost, records keep
 * queueing. Resuming asks first, because a live pipeline then works through everything that queued
 * while it was paused and creates transactions for it.
 */
export default function PipelineRunControl({ clientUid, pipeline, onChanged, size = 'medium' }: Props) {
  const { showToast } = useApi();
  const [busy, setBusy] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);

  const running = pipeline.state === 'LIVE' || pipeline.state === 'SHADOW';
  const stopped = pipeline.state === 'PAUSED' || pipeline.state === 'NEEDS_ATTENTION';
  if (!running && !stopped) return null;

  const run = async (action: 'pause' | 'resume') => {
    setBusy(true);
    try {
      const p = unwrap(await (action === 'pause' ? diApi.pause(clientUid, pipeline.uid) : diApi.resume(clientUid, pipeline.uid)));
      onChanged(p);
      showToast(action === 'pause' ? `${pipeline.name} paused. Records keep queueing.` : `${pipeline.name} resumed`, 'success');
      setConfirmResume(false);
    } catch (e) {
      showToast(errMsg(e, action === 'pause' ? 'Could not pause' : 'Could not resume'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    // Stops clicks (including those inside the portalled dialog) from reaching a clickable table row.
    <Box component="span" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      {running ? (
        <Button
          variant="outlined"
          color="warning"
          size={size}
          disabled={busy}
          onClick={() => run('pause')}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Pause
        </Button>
      ) : (
        <Button variant="outlined" color="primary" size={size} disabled={busy} onClick={() => setConfirmResume(true)}>
          Resume
        </Button>
      )}

      <Dialog open={confirmResume} onClose={busy ? undefined : () => setConfirmResume(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resume {pipeline.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It first works through the records that arrived while it was paused, then carries on as each change arrives. A live pipeline
            creates transactions for every matching record, so check the filter before resuming.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmResume(false)} disabled={busy} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => run('resume')}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
